import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, API_URL, capturedAuthParams } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Use pre-captured URL/sessionStorage params
  const [isRecoveryMode, setIsRecoveryMode] = useState(() => {
    return capturedAuthParams?.type === 'recovery';
  });
  const [recoveryError, setRecoveryError] = useState(() => {
    if (capturedAuthParams?.type === 'error') {
      return {
        error: capturedAuthParams.error,
        errorCode: capturedAuthParams.errorCode,
        errorDescription: capturedAuthParams.errorDescription,
      };
    }
    return null;
  });

  useEffect(() => {
    // Get initial session on mount (handles the "redeployed app, still has session" case)
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        // Token invalid / expired / malformed → clear stale client state.
        console.warn('[Auth] getSession error, clearing stale session:', error.message);
        clearStaleSession();
        setLoading(false);
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        console.log('[Auth] Found session for', session.user.email, '— fetching profile...');
        fetchProfile(session.access_token);
      } else {
        console.log('[Auth] No session on mount — user is anonymous.');
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Auth] state change:', event, session?.user?.email || '(none)');
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoveryMode(true);
        setRecoveryError(null);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.access_token);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Aggressive cleanup for stale sessions after redeploy, backend JWT secret rotation,
  // or user deletion. Clears Supabase auth + any app-level storage we might have written.
  // CRITICAL: We use scope='local' (or skip the network call entirely) to avoid:
  //   • invalidating the user's refresh token globally (which would block future signins)
  //   • racing a fire-and-forget signOut() against a fresh setSession() and wiping the new session
  const clearStaleSession = () => {
    // Skip the network signout call — just clear local state. The previous `supabase.auth.signOut()`
    // default scope was 'global', which invalidated the user's refresh token on the Supabase
    // server side and caused recurring "have to reset password after every redeploy" reports.
    setUser(null);
    setProfile(null);
    setSession(null);
    try {
      // Remove common Supabase-auth-token keys (format: sb-<project-ref>-auth-token)
      Object.keys(localStorage).forEach(k => {
        if (k.startsWith('sb-') && k.includes('auth-token')) localStorage.removeItem(k);
      });
    } catch { /* SSR / storage disabled */ }
  };

  const fetchProfile = async (token) => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
        setAcceptedTerms(!!data.accepted_terms);
        console.log('[Auth] profile loaded:', {
          id: data.profile?.id,
          email: data.profile?.email,
          role: data.profile?.role,
        });
      } else if (response.status === 401 || response.status === 403) {
        // Token has been invalidated (user deleted, backend JWT secret changed, etc.)
        // Do NOT leave the UI in a half-logged-in state.
        console.warn('[Auth] /auth/me returned', response.status, '→ clearing stale session.');
        clearStaleSession();
      } else {
        // Transient error (500, network). Keep session so the user isn't silently booted,
        // but leave profile null — consumers will treat user as "unknown role" (see Header).
        console.error('[Auth] /auth/me transient failure:', response.status);
        setProfile(null);
      }
    } catch (error) {
      console.error('[Auth] Error fetching profile:', error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async ({ email, password, fullName, role = 'customer', phone, acceptedTerms = false }) => {
    try {
      const response = await fetch(`${API_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          full_name: fullName,
          role,
          phone,
          accepted_terms: acceptedTerms
        })
      });
      let data = null;
      try {
        data = await response.json();
      } catch (parseErr) {
        // Body may have been consumed by upstream middleware; fall back to status-code mapping
        data = null;
      }
      if (!response.ok) {
        let detail = data && data.detail;
        if (!detail) {
          if (response.status === 409) detail = 'An account with this email already exists. Please sign in instead.';
          else if (response.status === 429) detail = 'Too many signup attempts. Please wait 10-15 minutes before trying again, or use a different email address.';
          else if (response.status === 400) detail = 'Please check your details and try again.';
          else detail = 'Signup failed. Please try again.';
        }
        throw new Error(detail);
      }
      if (!data) throw new Error('Server returned an invalid response. Please try again.');
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signIn = async ({ email, password }) => {
    try {
      const response = await fetch(`${API_URL}/auth/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      // The platform fetch interceptor consumes the body on non-2xx responses.
      // We try to parse json() regardless and fall back to status-code mapping
      // when the body has already been read.
      let data = null;
      try {
        data = await response.json();
      } catch (parseErr) {
        data = null;
      }
      if (!response.ok) {
        console.warn('[Auth] signin failed:', { status: response.status, hasData: !!data, detail: data?.detail });
        let detail = data && data.detail;
        if (!detail) {
          if (response.status === 401) detail = 'Invalid email or password. Please check your credentials and try again.';
          else if (response.status === 403) detail = 'Please verify your email before signing in.';
          else if (response.status === 429) detail = 'Too many sign-in attempts. Please wait a few minutes and try again.';
          else if (response.status >= 500) detail = `Server error (HTTP ${response.status}). Please try again in a moment.`;
          else if (response.status === 0) detail = 'Network error — please check your connection and try again.';
          else detail = `Sign-in failed (HTTP ${response.status}). Please try again.`;
        }
        throw new Error(detail);
      }
      if (!data) throw new Error('Server returned an invalid response. Please try again.');
      if (data.session?.access_token) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token
        });
        setProfile(data.profile);
      }
      return { data, error: null };
    } catch (error) {
      // Network/TypeError (Failed to fetch) — give the user a clearer hint.
      const msg = error?.message || '';
      if (/Failed to fetch|NetworkError|TypeError/i.test(msg)) {
        return { data: null, error: new Error('Could not reach the server. Please check your internet connection and try again.') };
      }
      return { data: null, error };
    }
  };

  const resendVerification = async (email) => {
    try {
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error('Server error. Please try again.');
      }
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend verification email');
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    console.log('[Auth] signOut invoked');
    try {
      // scope='local' clears only THIS browser's tokens. We do NOT invalidate the
      // refresh token on the Supabase server (which would cause "Have to reset
      // password every redeploy" if any race with a fresh signin happens, AND
      // would log the user out from any other devices they're signed in on).
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) {
      console.warn('[Auth] Supabase signOut threw, continuing with local cleanup:', e?.message);
    }
    setUser(null);
    setProfile(null);
    setSession(null);
    setAcceptedTerms(false);
    setIsRecoveryMode(false);
    setRecoveryError(null);
    // Flush any Supabase auth tokens that might outlive the signOut call
    // (happens if the tab was suspended / backgrounded during signOut).
    try {
      Object.keys(localStorage).forEach(k => {
        if (k.startsWith('sb-') && k.includes('auth-token')) localStorage.removeItem(k);
      });
      sessionStorage.removeItem('__recovery_error');
      sessionStorage.removeItem('__recovery_mode');
    } catch { /* storage disabled */ }
  };

  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const clearRecoveryMode = () => {
    setIsRecoveryMode(false);
    setRecoveryError(null);
    try {
      sessionStorage.removeItem('__recovery_error');
      sessionStorage.removeItem('__recovery_mode');
    } catch (e) { /* ignore */ }
  };

  const value = {
    user,
    profile,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resendVerification,
    resetPassword,
    updatePassword,
    refreshProfile: () => session?.access_token && fetchProfile(session.access_token),
    isAuthenticated: !!user,
    isRecoveryMode,
    recoveryError,
    clearRecoveryMode,
    acceptedTerms,
    isCustomer: profile?.role === 'customer',
    isProvider: profile?.role === 'provider',
    isAdmin: profile?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
